/**
 * MSG-7: оркестратор чата 1:1 в веб-ЛК владельца — веб-зеркало
 * ChatController Android (MSG-6). Слои под ним: olmClient (перенос эталона
 * MSG-5), MsgApi (REST через axios + SSE), MsgStore (IndexedDB).
 *
 * Инварианты (уроки MSG-6, обязательные к переносу — тикет §«уроки»):
 * 1. message_id рождается ДО первой попытки, конверт живёт в аутбоксе до
 *    ack; ретрай шлёт ТОТ ЖЕ конверт — сервер дедупит (duplicate=true = ok).
 * 2. Каждый входящий конверт — под своим try/catch: сервер недоверенный,
 *    битая строка не останавливает догон (это уже внутри receiveEnvelopes
 *    эталона: нерасшифрованное = plaintext null, не исключение).
 * 3. Отправитель сверяется строго со своим и peer-идентификатором.
 * 4. 401/403 REST лечит интерсептор axios (refresh); для SSE 401 — «тычок»
 *    poke() и реконнект. Терминален только 403 (нет прав на чат).
 * 5. «Повторить» поднимается только из восстановимых отказов; провал
 *    инициализации крипты — честная ошибка без кнопки.
 * 6. Бандл визави кэшируется (жжёт one-time каждым GET) — перечитывается
 *    только на старте и после 410.
 *
 * Все операции с криптомашиной сериализованы очередью _enqueue: OlmMachine
 * не переносит конкурентных вызовов (актор на Android, цепочка промисов тут).
 */
import {
  attachmentMeta, decryptAttachment, encryptFile, MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENTS_PER_MESSAGE, validMeta,
} from './attachments';
import { MsgStore } from './msgStore';
import { normalizeReplyTo } from './replyTo';
import { deviceIdentityLostError, deviceTakeoverError } from './deviceTakeover';
// 🔴 Единственный импорт из msgRuntime — ПРАВИЛО доверия и запрета отправки.
// Оно одно на оба контроллера намеренно (решение владельца 07.09: запрет
// отправки при смене ключа): две копии правила безопасности разошлись бы
// первой же правкой, и 1:1 с комнатой стали бы отвечать на один вопрос
// по-разному. Остальные помощники msgRuntime 1:1 по-прежнему не трогает.
import { tofuVerdict, trustBlocksSend } from './msgRuntime';
// Д-17: сторож «вышло обновление» — чистый модуль вне msg/, предметной
// области не несёт; сигнал «поток встал» идёт из обоих циклов SSE.
import { noteStreamReady } from '../utils/updateWatch';

const PASSPHRASE_KEY = 'msg_store_pass';
const RECONNECT_MAX_MS = 30_000;
// ATT C4: голый 404 на POST конверта = разговора или устройства визави нет
// (флаг выключен, собеседник удалён). Конверт остаётся, текст цел; текст один
// на обе точки POST — визави может быть сотрудником, участником или владельцем.
const NO_CONVERSATION_TEXT = 'Сообщение не ушло: сервер не нашёл разговор или '
  + 'устройство собеседника — обновите страницу и повторите';

/** Бросается из шагов bootstrap после stop(): start() глотает его молча —
 *  это не провал крипты, а отмена (страница ушла к другому визави). */
export class StoppedError extends Error {
  constructor() { super('ChatController остановлен'); this.name = 'StoppedError'; }
}

/**
 * Passphrase IndexedDB-стора машины. Живёт в localStorage рядом с did:
 * от XSS в том же origin это НЕ защита (политика MSG-5 — стор не должен быть
 * открытым: пароль отсекает чтение файла стора вне браузера и косые бэкапы
 * профиля). Потеря пары localStorage+IndexedDB = новое устройство (§2.1).
 */
function storePassphrase() {
  let pass = localStorage.getItem(PASSPHRASE_KEY);
  if (!pass) {
    const raw = new Uint8Array(32);
    globalThis.crypto.getRandomValues(raw);
    pass = Array.from(raw, (b) => b.toString(16).padStart(2, '0')).join('');
    localStorage.setItem(PASSPHRASE_KEY, pass);
  }
  return pass;
}


/**
 * Пауза автоповтора, пока визави не поднял крипту (kind='peer_no_device').
 * Ждём человека на той стороне, поэтому секунды, а не миллисекунды: каждая
 * попытка — полный перезапуск подъёма (лидерство, транспорт, машина).
 */
export const PEER_WAIT_RETRY_MS = 5000;

/**
 * Отказ bootstrap, переведённый на человеческий (живой прогон 25.08).
 *
 * 🔴 404 на `msg/devices/peer` — НЕ поломка и не редкость: сервер отвечает так,
 * пока визави ни разу не поднимал крипту (apis/msg.py: «клиент показывает это
 * состояние»). Устройство заводится при открытии ЧАТА, а не страницы «Мой QR»,
 * поэтому тот, кто открыл чат первым, упирается в это ШТАТНО — и раньше видел
 * «Крипта не поднялась: HTTP 404 msg/devices/peer …», то есть техническую
 * правду, из которой не следует ни одного действия.
 *
 * `kind` ведёт автоповтор на странице: ждать тут нечего, кроме собеседника,
 * и снимать ожидание руками (F5) человек не обязан.
 */
export function bootstrapError(e) {
  // 🔴 Д-20. Узнаём по ИМЕНИ, а не `instanceof DeviceTakeoverNeeded`:
  // класс живёт в olmClient, который тянет WASM 7,8 МБ и потому грузится
  // ТОЛЬКО динамически (loadCrypto). Статический импорт ради instanceof
  // затащил бы крипту в основной бандл — то, от чего дизайн §6 уходил.
  // 🔴 Участнику по приглашению этот отказ прийти не может: его did живёт в
  // member-JWT и всегда равен `join_did` (сервер сверяет их в `_actor`), то
  // есть активное устройство для него всегда «своё». Это важно, потому что
  // страница участника рисует нерепитабельные отказы простой красной
  // плашкой — вопрос «Перенести сюда?» он увидел бы БЕЗ кнопки.
  if (e?.name === 'DeviceTakeoverNeeded') return deviceTakeoverError(e.info);
  // Д-3 «хвост»: крипто-стор погиб, did цел (409 на регистрации, дважды).
  // Тоже по ИМЕНИ и по той же причине, что выше: класс живёт в olmClient.
  if (e?.name === 'DeviceIdentityLost') return deviceIdentityLostError();
  const onPeer = String(e?.path ?? '').startsWith('msg/devices/peer');
  // 🔴 410 — собеседника УДАЛИЛИ (сервер: detail.code = peer_gone). Ждать
  // нечего и некого: это состояние ожиданием не лечится, поэтому оно обязано
  // отличаться от 404 и текстом, и поведением страницы — без автоповтора и с
  // отпусканием лидерства вкладки. Ровно на этом сорвался прогон 25.08:
  // вкладка вечно «ждала собеседника» на переписке с удалённым участником и
  // не пускала в остальные чаты.
  if (e?.status === 410 && onPeer) {
    return {
      kind: 'peer_gone',
      text: 'Собеседника больше нет — он удалён, и переписка закрыта. '
        + 'Отправить сюда ничего нельзя; история осталась на вашем устройстве.',
      retriable: false,
    };
  }
  if (e?.status === 404 && onPeer) {
    return {
      kind: 'peer_no_device',
      text: 'Собеседник ещё не открыл чат — попросите его зайти. '
        + 'Как только он откроет, чат поднимется здесь сам.',
      retriable: false,
    };
  }
  return { text: `Крипта не поднялась: ${e?.message ?? e}`, retriable: false };
}

/**
 * Отказ загрузки вложения, переведённый на человеческий. retriable=false
 * всюду: «Повторить» гонит аутбокс, а сорвавшаяся загрузка в аутбокс не
 * попала — черновик (текст и файл) вернула страница, отправить заново = клик.
 */
export function uploadError(e) {
  if (e?.code === 'too_large' || e?.status === 413) {
    return {
      text: `Файл больше ${MAX_ATTACHMENT_BYTES / (1024 * 1024)} МБ — отправить его не получится.`,
      retriable: false,
    };
  }
  if (e?.status === 507) {
    // Сервер объясняет квоту сам (числом из своего конфига) — его текст точнее
    return {
      text: typeof e.detail === 'string'
        ? e.detail
        : 'Хранилище вложений переполнено — место освободится, когда уйдут старые переписки.',
      retriable: false,
    };
  }
  return { text: 'Вложение не загрузилось — проверьте связь и отправьте заново', retriable: false };
}

/**
 * Имя IndexedDB-стора криптомашины — по ПРИНЦИПАЛУ и устройству.
 *
 * 🔴 Для владельца форма обязана остаться прежней (`total-msg-o<id>-<did>`):
 * смена имени = новый пустой стор = новое устройство и «ключ изменился» у
 * всех собеседников разом. Префиксы — те же, что в matrixUserId и на
 * Android (PrincipalSession.PREFIXES): один словарь на все три места.
 */
export function storeNameFor(principal, did) {
  const prefix = { owner: 'o', employee: 'e', member: 'm' }[principal?.type];
  if (!prefix || !Number.isInteger(principal.id) || principal.id <= 0) {
    throw new Error(`не могу построить имя стора: ${JSON.stringify(principal)}`);
  }
  return `total-msg-${prefix}${principal.id}-${did}`;
}

export class ChatController {
  /**
   * @param api        MsgApi
   * @param ownerId    id владельца из /website/me (matrix @o<id>)
   * @param did        browserDeviceId()
   * @param loadCrypto () => import('./olmClient') — единственная точка,
   *                   тянущая WASM-чанк; тесты подставляют фейк
   * @param onUpdate   (state) => void — снимок состояния для страницы
   * @param storeFactory для тестов (fake-IndexedDB)
   * @param principal  QR-3: КТО говорит в этой вкладке — {type, id}. По
   *                   умолчанию владелец в своём ЛК (прежнее поведение);
   *                   участник по приглашению приходит со своим принципалом
   *                   и работает в браузере без установки приложения.
   */
  constructor({
    api, ownerId, did, loadCrypto, onUpdate, storeFactory = null, principal = null,
    allowTakeover = false,
  }) {
    this.api = api;
    this.ownerId = ownerId;
    this.principal = principal ?? { type: 'owner', id: ownerId };
    this.did = did;
    // Д-20: человек нажал «перенести сюда» — вытеснять чужое устройство можно.
    this.allowTakeover = allowTakeover;
    this.loadCrypto = loadCrypto ?? (() => import('./olmClient'));
    this.onUpdate = onUpdate ?? (() => {});
    this.storeFactory = storeFactory ?? (() => new MsgStore());
    this.client = null;
    this.store = null;
    this.stream = null;
    this.stopped = false;
    this._queue = Promise.resolve();
    this._catchUpQueued = false;
    // Устаревшие device id из истории (ревью р.2, Medium): по каждому чужому
    // sender_device_id бандл перечитывается ОДИН раз за жизнь контроллера —
    // каждый GET бандла жжёт one-time ключ визави, цикл по строкам истории
    // с мёртвого устройства выжег бы пул.
    this._staleSenders = new Set();
    this.state = {
      phase: 'init',           // init | ready | error
      error: null,             // {text, retriable}
      messages: [],            // хронология для рендера (store + аутбокс)
      trust: null,             // {status: first_use|unchanged|key_changed|verified, verified, acked}
      safetyNumber: null,
      peer: null,
      // Запрет отправки (решение владельца 07.09): ключ визави СМЕНИЛСЯ и
      // человек ещё не решил — сверить код или отправить всё равно.
      // null | [{principalKey, name}] — форма ОДНА с комнатой, чтобы экран
      // участника рисовал обе одной строкой.
      sendBlocked: null,
      // ATT С2: идущая загрузка вложения — {name, size, percent|null}.
      // percent null = транспорт не отдаёт прогресса (тесты, старый axios).
      uploading: null,
    };
  }

  _emit(patch) {
    // После stop() страница уже размонтирована/перешла к другому визави
    // (ревью QR-1b р.2, High): хвост async-работы не должен трогать UI
    if (this.stopped) return;
    this.state = { ...this.state, ...patch };
    this.onUpdate(this.state);
  }

  /** Сигнал «остановлен посреди bootstrap» — не ошибка, а штатный обрыв. */
  _checkStopped() {
    if (this.stopped) throw new StoppedError();
  }

  /** Сериализация операций с машиной: цепочка вместо конкурентных вызовов. */
  _enqueue(fn) {
    const next = this._queue.then(() => (this.stopped ? undefined : fn()));
    // Разрыв цепочки навсегда — деградация хуже пропуска: ошибку съедаем
    // здесь, а наружу её несёт сам fn через _emit.
    this._queue = next.catch(() => {});
    return next;
  }

  // ------------------------------------------------------------- старт

  /**
   * @param peer  number = users.user_id сотрудника (MSG-7, как было) ИЛИ
   *              {type: 'employee'|'member', id} (QR-1b: участник по приглашению —
   *              ensure телом {member_id}; сотрудник — {user_id}).
   *
   * Провал bootstrap разбирает bootstrapError: страница показывает человеку
   * действие, а не HTTP-код (живой прогон 25.08).
   */
  async start(peer) {
    try {
      await this._enqueue(() => this._bootstrap(peer));
      if (this.stopped || this.state.phase === 'error') return;
      this._emit({ phase: 'ready' });
      this._streamLoop(); // фоновый цикл, не await
    } catch (e) {
      // Остановлены посреди подъёма (смена визави / уход со страницы) — не
      // ошибка; _emit после stop и так no-op, но и падать наружу незачем
      if (e instanceof StoppedError || this.stopped) return;
      // Провал инициализации крипты НЕ прячем (урок 5): без ключей чат
      // не работает, «повторить» не поможет — нужна перезагрузка/разбор.
      this._emit({ phase: 'error', error: bootstrapError(e) });
    }
  }

  /**
   * Тело ensure по описанию визави (вынесено ради пина тестом).
   *
   * 🔴 Участник по приглашению визави НЕ ВЫБИРАЕТ: сервер знает его пару из
   * токена и ЗАПРЕЩАЕТ `user_id`/`member_id` в теле (apis/msg.py) — попытка
   * прислать их вернула бы 4xx, а не чат.
   */
  static ensureBody(peer, principalType = 'owner') {
    if (principalType === 'member') return {};
    if (typeof peer === 'number') return { user_id: peer };
    if (peer && typeof peer === 'object' && Number.isInteger(peer.id) && peer.id > 0) {
      if (peer.type === 'member') return { member_id: peer.id };
      if (peer.type === 'employee') return { user_id: peer.id };
    }
    throw new Error(`ChatController.start: непонятный визави ${JSON.stringify(peer)}`);
  }

  async _bootstrap(peer) {
    const body = ChatController.ensureBody(peer, this.principal.type);
    // Между шагами — проверка stop() (ревью QR-1b р.2, High): после ухода
    // страницы bootstrap обязан ОБОРВАТЬСЯ, а не дожимать машину/стор в фоне —
    // страница отпускает Web Lock только после нашего stop(), и следующий
    // контроллер откроет ТОТ ЖЕ IndexedDB-стор; две машины на одном сторе =
    // порча Olm-состояния (политика MSG-5/MSG-7)
    const { TotalMsgClient } = await this.loadCrypto();
    this._checkStopped();
    this.store = await this.storeFactory().open();
    this._checkStopped();
    this.client = new TotalMsgClient(this.api, {
      principalType: this.principal.type, principalId: this.principal.id, did: this.did,
      allowTakeover: this.allowTakeover,
    });
    await this.client.init({
      storeName: storeNameFor(this.principal, this.did),
      passphrase: storePassphrase(),
    });
    this._checkStopped();
    await this.client.publishKeys();
    this._checkStopped();
    await this._topUpPrekeys();
    this._checkStopped();
    await this.client.ensureConversation(body);
    this._checkStopped();
    // peer в снимке — с именем визави из ensure (QR-1: label участника для
    // заголовка чата; у сотрудника name = null, страница берёт ФИО из списка)
    this._emit({ peer: { ...this.client.peer, name: this.client.peerName ?? null } });

    await this.client.fetchPeerBundle();
    this._checkStopped();
    await this._tofuCheck(); // он же считает и эмитит safety number
    this._checkStopped();
    await this.client.ensureSession();
    this._checkStopped();

    await this._loadHistory();
    await this._resendOutbox();
    this._checkStopped();
    // Ошибка ПЕРВОГО догона — не «крипта не поднялась»: машина уже жива,
    // чат работает, конверты ждут на сервере — честная плашка и ретрай.
    await this._catchUp().catch(() => {
      this._emit({ error: { text: 'Приём сообщений прервался — повторите или подождите переподключения', retriable: true } });
    });
  }

  /**
   * Долив своего пула одноразовых ключей — при КАЖДОМ открытии чата (долг
   * QR-2 №1, зеркало `roomController._topUpPrekeys`).
   *
   * 🔴 Зачем в 1:1, где машина и так одна на устройство. Пул один на оба
   * тракта: ключи из него жгут и визави в личном чате, и КАЖДЫЙ, кто заводит
   * с нами Olm-сессию в комнате. Правду об остатке знает только сервер —
   * машина считает лишь то, что залила сама. До этого шага она слышала
   * счётчик ТОЛЬКО из ответа на свою заливку, а после перезагрузки вкладки —
   * пустую Map (`lastOtkCount === null`), и заливала полную пачку заново:
   * замер стенда 02.09 — ровно +50 ключей на каждую перезагрузку (100 у
   * владельцев MSG-7 после одного reload; с пробой — 51). Что именно машина
   * делает с пустой Map внутри wasm, по коду не проверено (ревью с.88) — и
   * не важно: честный счётчик от сервера хуже сделать не может. Проба стоит
   * ДО `ensureSession`, который кормит машину тем же счётчиком.
   *
   * 🔴 Что пустой пул значит на самом деле (проверено инструментом 02.09,
   * стенд + pytest): у устройства, чей подъём хоть раз дошёл до
   * `ensureSession`, на сервере лежит fallback-ключ — машина рождает его,
   * услышав «неиспользованных fallback нет». Значит исчерпание пула — это
   * деградация (общий ключ для всех новых сессий, метрика `bundles.fallback`
   * растёт), а НЕ остановка отправки. Без fallback остаётся только устройство,
   * подъём которого оборвался до бандла визави; долив здесь стоит раньше и
   * закрывает и его.
   *
   * 🔴 Отказ долива подъём НЕ рушит. Читать чат можно и с пустым пулом, а
   * настоящую беду покажут следующие шаги: они ходят тем же транспортом в
   * тот же сервер и говорят о ней своими словами (`bootstrapError`). Свои у
   * долива были бы заведомо неверными — 403 здесь значит «нашего устройства
   * нет или оно ревокнуто» (`_require_sender_device`), а не «собеседник ещё
   * не открыл чат»; бандл визави следом упадёт тем же 403 и скажет об этом
   * правильными словами.
   */
  async _topUpPrekeys() {
    try {
      await this.client.topUpPrekeys();
    } catch {
      /* см. выше: диагноз ставят следующие шаги подъёма */
    }
  }

  // ------------------------------------------------------------- доверие

  /** TOFU §2.1: identity визави пиняется при первом контакте; смена ключа —
   *  видимое предупреждение и сброс «проверено» (критерий 5). Вместе с ключом
   *  пиняется deviceRowId (msg_devices.id) — по нему ВХОДЯЩИЙ конверт с
   *  нового устройства ловится без 410 (ревью р.1, High-1).
   *
   *  🔴 С 07.09 приговор считает ОБЩИЙ `tofuVerdict` (правило одно с
   *  комнатой), и «ключ СМЕНИЛСЯ» — липкое состояние: живёт в пине
   *  (`changedAt`) до «Код сверен», переживая перечитку бандла и
   *  перезагрузку вкладки. Пока оно не снято и не подтверждено «Отправить
   *  всё равно» (`ackedAt`), отправка стоит (`sendBlocked`). */
  async _tofuCheck() {
    const b = this.client.peerBundle;
    const ed25519 = b.device_keys?.keys?.[`ed25519:${b.did}`];
    if (!ed25519) throw new Error('в бандле визави нет ed25519-ключа');
    const key = `peer-identity:${this.client.conversationId}`;
    const known = await this.store.getKV(key);
    const verdict = tofuVerdict(known, ed25519);
    if (verdict.repin) {
      // При смене ключа `ackedAt` и `verifiedAt` НЕ переезжают в новый пин
      // намеренно: и «сверено», и «отправить всё равно» были сказаны про
      // ПРЕЖНИЙ ключ. Новый ключ — новое решение.
      await this.store.setKV(key, {
        ed25519,
        did: b.did,
        deviceRowId: b.device_id,
        verifiedAt: null,
        ...(verdict.changed ? { changedAt: Date.now() } : {}),
      });
    } else if (known.deviceRowId !== b.device_id) {
      await this.store.setKV(key, { ...known, deviceRowId: b.device_id });
    }
    const trust = {
      status: verdict.status,
      verified: verdict.status === 'verified',
      // Свежая смена (verdict.changed) сбрасывает прежний ack вместе с пином;
      // липкое состояние несёт ack из пина — человек уже решал.
      acked: !verdict.changed && Boolean(known?.ackedAt),
    };
    // Safety number пересчитывается ВМЕСТЕ с доверием (ревью р.2, High-A):
    // после смены бандла на экране не имеет права остаться старый код —
    // владелец сверил бы прежнее число и подтвердил НОВЫЙ ключ.
    const safetyNumber = await this.client.safetyNumber();
    this._emit({
      trust,
      safetyNumber,
      sendBlocked: trustBlocksSend(trust)
        ? [{ principalKey: null, name: this.client.peerName ?? null }]
        : null,
    });
  }

  /** Ревью р.1, High-1: сотрудник переустановился и написал ПЕРВЫМ — 410 не
   *  будет, единственный след нового устройства — sender_device_id входящего
   *  конверта. Расхождение с пином = событие доверия: свежий бандл, TOFU
   *  (баннер + сброс «проверено») и новая сессия ДО записи текста в историю. */
  async _noteSenderDevice(row) {
    if (!row.sender_device_id) return;
    const p = this.client.peer;
    if (!p || row.sender_type !== p.type || row.sender_id !== p.id) return;
    const known = await this.store.getKV(`peer-identity:${this.client.conversationId}`);
    if (!known?.deviceRowId || known.deviceRowId === row.sender_device_id) return;
    // Ревью р.2, Medium: бандл отдаёт ТЕКУЩЕЕ активное устройство — строка
    // из истории с давно ревокнутого может с ним и не совпасть; перечитка
    // на каждую такую строку жгла бы one-time. Один разбор на device id.
    if (this._staleSenders.has(row.sender_device_id)) return;
    await this.client.fetchPeerBundle();
    await this._tofuCheck();
    await this.client.ensureSession();
    // Пометка ТОЛЬКО после успешного разбора (ревью р.3, High): transient-
    // отказ сети/стора здесь пробрасывается, last-seq не двигается — и ретрай
    // обязан снова прийти в refresh, а не проскочить мимо по «забаненному» id.
    this._staleSenders.add(row.sender_device_id);
  }

  /** Владелец сверил числа голосом и нажал «подтвердить». Сверка снимает и
   *  запрет отправки: зависший в аутбоксе текст досылается сам — человек уже
   *  нажимал «Отправить», второй раз просить его незачем.
   *
   *  🔴 Ревью WEB-14 р.1, High. Раньше метод жил ВНЕ очереди и делал
   *  read-modify-write пина напрямую: он мог гоняться с перепином из
   *  `_noteSenderDevice`/`_retryAfterGone` и дописать СТАРЫЙ пин поверх
   *  свежего `{ed25519: NEW, changedAt}` — стерев и тревогу, и запрет для
   *  ключа, которого человек в глаза не видел. Теперь шаг идёт через
   *  `_enqueue`, а сверка привязана к КОДУ, который человек сверял: снимок
   *  `safetyNumber` берётся в момент клика, и если к исполнению шага код уже
   *  другой — сверка не применяется (на экране к этому моменту стоит свежая
   *  тревога с новым кодом). */
  markVerified() {
    const expected = this.state.safetyNumber;
    return this._enqueue(async () => {
      if (!expected || expected !== await this.client.safetyNumber()) return;
      const key = `peer-identity:${this.client.conversationId}`;
      const known = await this.store.getKV(key);
      if (!known) return;
      await this.store.setKV(key, { ...known, verifiedAt: Date.now() });
      this._emit({
        trust: { status: 'verified', verified: true, acked: Boolean(known.ackedAt) },
        sendBlocked: null,
      });
      if (await this._resendOutbox()) await this._loadHistory();
    }).catch(() => {
      this._emit({ error: { text: 'Не удалось сохранить сверку — повторите', retriable: false } });
    });
  }

  /**
   * «Отправить всё равно» — решение владельца 07.09: смена ключа СТАВИТ
   * отправку, но человек вправе осознанно продолжить. Подтверждение пишется
   * в пин (`ackedAt`) и потому переживает перезагрузку; тревога при этом НЕ
   * снимается — плашка остаётся красной до настоящей сверки. Новая смена
   * ключа перепиняет запись и потребует нового решения.
   */
  sendAnyway() {
    // 🔴 Ревью WEB-14 р.2, High: снимок кода — В МОМЕНТ КЛИКА. «Всё равно»
    // сказано про предупреждение, которое человек видел; если, пока клик
    // стоял в очереди, ключ перепинился ещё раз, решение не применяется —
    // на экране уже свежая тревога, решать надо заново.
    const expected = this.state.safetyNumber;
    return this._enqueue(async () => {
      if (!this.state.sendBlocked) return;
      if (!expected || expected !== await this.client.safetyNumber()) return;
      const key = `peer-identity:${this.client.conversationId}`;
      const known = await this.store.getKV(key);
      if (!known) return;
      await this.store.setKV(key, { ...known, ackedAt: Date.now() });
      this._emit({
        trust: { ...this.state.trust, acked: true },
        sendBlocked: null,
      });
      if (await this._resendOutbox()) await this._loadHistory();
    }).catch(() => {
      this._emit({ error: { text: 'Не удалось подтвердить отправку — повторите', retriable: false } });
    });
  }

  // ------------------------------------------------------------- история

  async _loadHistory() {
    const rows = await this.store.listMessages(this.client.conversationId);
    const pending = await this.store.listOutbox(this.client.conversationId);
    this._emit({
      messages: [
        ...rows.map((r) => ({ ...r, pending: false })),
        ...pending.map((p) => ({
          conversationId: p.conversationId, messageId: p.messageId,
          seq: null, direction: 'out', text: p.text, sentAt: p.sentAt,
          attachments: p.attachments ?? null,
          replyTo: p.replyTo ?? null,
          pending: true,
        })),
      ],
    });
  }

  // ------------------------------------------------------------- отправка

  /**
   * Отправить текст, с вложениями или без. Ошибки не бросает — несёт их через
   * state; `false` = ничего не ушло, странице вернуть черновик.
   *
   * @param files File[] — вложения. Порядок ATT С2 обязателен: сначала блобы
   * на сервер (upload -> attachment_id), потом метаданные внутрь шифротекста,
   * потом конверт. Обратный дал бы ссылку на несуществующий блоб.
   * @param replyTo — снимок цитируемого сообщения (WEB-16) или null.
   */
  send(text, files = [], replyTo = null) {
    if (!files?.length) {
      return this._enqueueSend(() => this._sendStep(text, null, replyTo));
    }
    return this._sendWithFiles(text, files, replyTo);
  }

  /**
   * 🔴 Очередь ДЛЯ ОТПРАВКИ: `_enqueue` на остановленной машине возвращает
   * `undefined`, а контракт `send()` обещает странице `false` = «ничего не
   * ушло, верните черновик». Разъезд стоил бы ровно того, ради чего контракт
   * и заводился: участник нажимает «Отправить» с файлом, во время загрузки
   * переключается на общую комнату (это останавливает контроллер), и текст с
   * файлом исчезают молча — без ошибки, без записи в аутбоксе. Приводим
   * ответ к контракту здесь, а не проверкой `undefined` на двух страницах:
   * иначе следующая страница забудет про этот случай снова.
   * Общий `_enqueue` не трогаем — у markVerified/sendAnyway `undefined`
   * осмыслен (ревью 12.09, р.6, Medium; третий BLOCK по одной линии —
   * лечим ПОСЫЛКУ, а не третью заплату).
   *
   * 🔴 Чего это НЕ даёт (чтобы не обещать лишнего): черновик возвращается
   * только в ЖИВОЙ странице. Уход со страницы и смена визави размонтируют
   * компонент — вернуть текст будет некуда, и это ограничение по
   * построению, а не недосмотр (ревью р.7). Живой случай, ради которого
   * контракт и чинился, — переключение вкладки участником: там компонент
   * тот же, а контроллер меняется.
   */
  _enqueueSend(fn) {
    return Promise.resolve(this._enqueue(fn))
      .then((sent) => (sent === undefined ? false : sent))
      // 🔴 И падение шага — тоже `false`, а не reject. Страницы вешают
      // `.catch(() => {})` (иначе необработанный reject), поэтому брошенная
      // ошибка была для них НЕОТЛИЧИМА от успеха: текст человека исчезал
      // молча. Отказ при этом остаётся ВИДИМЫМ — строку показываем здесь,
      // раз наверх ошибка больше не идёт.
      .catch(() => {
        this._emit({
          error: { text: 'Сообщение не ушло — попробуйте ещё раз', retriable: false },
        });
        return false;
      });
  }

  /**
   * Шифрование и загрузка файлов идут ВНЕ очереди машины: 25 МБ по мобильной
   * сети — это минуты, и держать ими догон с приёмом нельзя. Машине от этого
   * шага ничего не нужно — WebCrypto и REST живут сами по себе.
   */
  async _sendWithFiles(text, files, replyTo = null) {
    // Пред-проверка запрета ДО шифрования и загрузки — та же экономика, что у
    // квоты на сервере: не гнать 25 МБ ради отказа в конце. Повторная проверка
    // будет в _sendStep: запрет мог встать, ПОКА файл грузился, — тогда блоб
    // останется без ссылки и его приберёт серверный свип сирот (24 ч).
    if (this.state.sendBlocked) {
      this._emitSendBlockedRefusal();
      return false;
    }
    const metas = [];
    try {
      for (const file of files) {
        this._emit({ uploading: { name: file.name || 'файл', size: file.size, percent: null } });
        const { ciphertext, meta } = await encryptFile(file);
        const ack = await this.api.uploadAttachment(ciphertext, {
          onUploadProgress: (ev) => {
            if (!ev?.total) return;
            this._emit({
              uploading: {
                name: meta.name, size: meta.size,
                percent: Math.min(100, Math.round((ev.loaded / ev.total) * 100)),
              },
            });
          },
        });
        metas.push(attachmentMeta({ attachmentId: ack.attachment_id, ...meta }));
      }
    } catch (e) {
      this._emit({ uploading: null, error: uploadError(e) });
      return false;
    }
    this._emit({ uploading: null });
    // Тот же честный контракт: пока файл грузился, контроллер могли
    // остановить (переключение вкладки у участника, уход со страницы).
    return this._enqueueSend(() => this._sendStep(text, metas, replyTo));
  }

  _emitSendBlockedRefusal() {
    this._emit({
      error: {
        text: 'Не отправлено: ключ устройства собеседника сменился. '
          + 'Сверьте код или подтвердите отправку — и отправьте заново.',
        retriable: false,
      },
    });
  }

  /** Шаг очереди: конверт -> аутбокс -> POST. attachments уже с id. */
  async _sendStep(text, attachments, replyTo = null) {
    // 🔴 Пояс к запрету (решение 07.09): страница гасит кнопку сама, но
    // смена ключа могла обнаружиться догоном, пока клик уже летел. Шифровать
    // на непризнанный ключ нельзя. Отказ ВИДИМ, а `false` говорит странице
    // вернуть черновик в поле ввода — она очистила его до вызова (ревью
    // WEB-14 р.1, Medium: молчаливый отказ съедал текст человека).
    if (this.state.sendBlocked) {
      this._emitSendBlockedRefusal();
      return false;
    }
    const sentAt = Date.now();
    // WEB-16: чистим цитату СВОИМ же контрактом, а не доверяем странице.
    // Правило одно на оба конца (см. replyTo.js): кап, который соблюдает
    // только отправитель, не защищает никого — и наш собственный экран тут
    // ничем не надёжнее чужого клиента.
    const quote = normalizeReplyTo(replyTo);
    const envelope = await this.client.encryptEnvelope(text, {
      sentAt, ...(attachments?.length ? { attachments } : {}),
      ...(quote ? { replyTo: quote } : {}),
    });
    const entry = {
      messageId: envelope.message_id,
      conversationId: this.client.conversationId,
      envelope, text, sentAt,
      // 🔴 Цитата живёт в аутбоксе рядом с конвертом по той же причине, что и
      // метаданные вложений: досыл после 410 и переезд на новое устройство
      // (Д-20) шифруют конверт ЗАНОВО — без неё ответ молча превратился бы в
      // обычное сообщение, и человек узнал бы об этом от собеседника.
      ...(quote ? { replyTo: quote } : {}),
      // 🔴 Д-20: ЧЬИМ устройством слеплен конверт. Аутбокс лежит в общей базе
      // `total-msg`, к did не привязанной, а конверт хранится ГОТОВЫМ и
      // досылается дословно. После «перенести сюда» (новый did = новая
      // Olm-сессия) прежний конверт ушёл бы шифротекстом мёртвой сессии, а
      // сервер проштамповал бы его новым отправителем: собеседник получил бы
      // нерасшифровываемое, а мы — «доставлено». Штамп позволяет досылу
      // увидеть чужой конверт и перешифровать его (_reEncryptOwn).
      did: this.did,
      // Метаданные (с ключами) живут в аутбоксе вместе с конвертом: досыл
      // после 410 перешифрует их в НОВЫЙ конверт — без них файл потерялся бы
      // из сообщения молча.
      ...(attachments?.length ? { attachments } : {}),
    };
    await this.store.putOutbox(entry); // конверт на диске ДО первой попытки
    // 🔴 ГРАНИЦА ответственности. С этой строки сообщение НЕ ПОТЕРЯНО: конверт
    // лежит на диске и будет досылаться сам (при следующем старте или по
    // «Повторить»). Значит любая ошибка дальше — про ПОКАЗ, а не про отправку,
    // и отвечать на неё `false` нельзя: страница вернула бы черновик, человек
    // отправил бы второй раз, и вышел бы дубль — сервер дедупит по
    // `message_id`, а у второй отправки он НОВЫЙ (ревью 12.09, р.7, Medium).
    // 🔴 Показ и отправка разделены (ревью р.8). Два правила:
    // 1. сбой ПЕРВОГО перечитывания не имеет права отменить сам POST —
    //    сообщение важнее того, успела ли лента перерисоваться;
    // 2. сбой показа не затирает ПРИГОВОР СЕРВЕРА. К этому моменту POST мог
    //    вернуть 409 (чужой message_id), 404 по сметённому блобу или
    //    peer_gone — конверт СНЯТ с очереди, и обещать «сохранено и будет
    //    отправлено» поверх такого исхода значит врать человеку.
    const noteDisplayGlitch = () => {
      if (this.state.error) return; // доменный отказ точнее и уже на экране
      this._emit({
        error: {
          text: 'Сообщение сохранено и будет отправлено, но экран не обновился — обновите страницу',
          retriable: true,
        },
      });
    };
    try { await this._loadHistory(); } catch { noteDisplayGlitch(); }
    try { await this._postOutboxEntry(entry); } catch { noteDisplayGlitch(); }
    try { await this._loadHistory(); } catch { noteDisplayGlitch(); }
    return true;
  }

  /**
   * ATT С2: скачать и расшифровать вложение. Очередь машины не нужна — REST и
   * WebCrypto. Ошибки бросает КОДАМИ (`sha_mismatch`, `undecryptable`,
   * `bad_meta`, MsgApiError.status) — текст подбирает экран: ему виднее,
   * рядом с картинкой это случилось или под кнопкой «Скачать».
   */
  async downloadAttachment(meta) {
    const bytes = await this.api.downloadAttachment(meta.id);
    return decryptAttachment(bytes, meta);
  }

  /** @returns {Promise<boolean>} конверт РЕАЛЬНО ушёл (settle), а не просто был
   *  опробован. На этом стоит решение перечитывать ли историю. */
  async _postOutboxEntry(entry) {
    // 🔴 Д-20: конверт, слепленный ПРЕЖНИМ устройством этого браузера
    // (человек нажал «перенести сюда»), отправлять нельзя — он зашифрован
    // мёртвой Olm-сессией, а сервер проштампует его НАШИМ device_id, и
    // «доставлено» окажется ложью. Перешифровываем тем же приёмом, что и
    // после 410: тот же message_id, тот же текст, те же вложения.
    // Записи без штампа — из времён до Д-20, они наши по построению
    // (ротации did тогда не существовало).
    if (entry.did && entry.did !== this.did) {
      const fresh = await this._reEncryptOwn(entry);
      if (!fresh) return false;
      entry = fresh;
    }
    try {
      const ack = await this.api.postEnvelope(entry.envelope);
      await this._settleSent(entry, ack);
      return true;
    } catch (e) {
      if (e?.status === 410) {
        return this._retryAfterGone(entry);
      }
      if (e?.status === 409) {
        // Коллизия чужого message_id (bd92dc05): наш ретрай этим id вечен и
        // бесполезен — конверт снимается, владельцу честная ошибка.
        await this.store.deleteOutbox(entry.messageId);
        this._emit({ error: { text: 'Сообщение отбито сервером (коллизия id) — отправьте заново', retriable: false } });
        // Конверт снят с очереди — список изменился, перерисовать надо
        return true;
      }
      // 🔴 ТОЛЬКО по машинному коду (ATT C4, ревью С3 р.2 High): сервер 1:1
      // отвечает голым 404 и на разговор, и на устройство, и на недостачу
      // вложений. Статус-only эвристика «404 + вложения = свип» съедала бы
      // текст человека с ложным «прикрепите заново», когда исчез РАЗГОВОР
      // (флаг выключен, собеседник удалён) — файл при этом жив.
      if (e?.status === 404 && e?.detail?.code === 'attachment_missing') {
        return this._dropSweptAttachments(entry);
      }
      if (e?.status === 404) {
        // Голый 404 — разговора или устройства нет: конверт ОСТАЁТСЯ, текст
        // цел; текст плашки честный, а не «проверьте связь».
        this._emit({ error: { text: NO_CONVERSATION_TEXT, retriable: true } });
        return false;
      }
      // Сеть/5xx/прочее: конверт остаётся в аутбоксе, «повторить» уместен.
      this._emit({ error: { text: 'Сообщение не ушло — проверьте связь и повторите', retriable: true } });
      return false;
    }
  }

  /**
   * Д-20: пересобрать СВОЙ зависший конверт после переноса чата на это
   * устройство. Возвращает обновлённую запись или null, если сейчас нельзя.
   *
   * 🔴 Запрет отправки уважаем так же, как в `_retryAfterGone`: перешифровка
   * на непризнанный ключ — ровно то, от чего заводился WEB-14. Конверт при
   * этом остаётся в аутбоксе: текст человека не теряется, досыл придёт сюда
   * же после «Код сверен» или «Отправить всё равно».
   */
  async _reEncryptOwn(entry) {
    if (this.state.sendBlocked) return null;
    await this.client.ensureSession();
    const envelope = await this.client.encryptEnvelope(entry.text, {
      messageId: entry.messageId,
      sentAt: entry.sentAt,
      ...(entry.attachments?.length ? { attachments: entry.attachments } : {}),
      ...(entry.replyTo ? { replyTo: entry.replyTo } : {}),
    });
    const updated = { ...entry, envelope, did: this.did };
    await this.store.putOutbox(updated);
    return updated;
  }

  /** 410: устройство визави ревокнуто (переустановка телефона). Свежий
   *  бандл -> TOFU (почти наверняка key_changed) -> новая сессия -> тот же
   *  message_id новым шифротекстом. Второй 410 подряд не зацикливаем. */
  async _retryAfterGone(entry) {
    // 🔴 410 от отправки значит «устройство получателя ревокнуто» — но ровно
    // так же выглядит и УДАЛЁННЫЙ собеседник: устройства ревокуются вместе с
    // ним. Отличает их только свежий бандл (ревью Codex р.2, Medium). Без
    // этой ветки отказ бандла улетал НАРУЖУ, очередь глотала его молча, и
    // человек видел вечное «отправляется…», а вкладка продолжала держать
    // лидерство на мёртвом чате — то самое, из-за чего сорвался прогон 25.08.
    try {
      await this.client.fetchPeerBundle();
    } catch (e) {
      const failed = bootstrapError(e);
      if (failed.kind !== 'peer_gone') throw e;
      // phase обязателен: терминальный экран страницы смотрит именно на него,
      // а приговор (и отпускание лидерства) — на kind.
      this._emit({ phase: 'error', error: failed });
      return false;  // конверт остаётся в очереди: текст человека не теряем
    }
    await this._tofuCheck();
    // 🔴 Решение 07.09: 410 почти всегда означает НОВЫЙ ключ визави, и до
    // этой строки зависший текст перешифровывался на него МОЛЧА — ровно то,
    // от чего запрет и заводился. Конверт остаётся в аутбоксе; после «Код
    // сверен» или «Отправить всё равно» досыл придёт сюда же и пройдёт.
    if (this.state.sendBlocked) return false;
    await this.client.ensureSession();
    // Вложения из аутбокса переезжают в новый шифротекст: блобы на сервере
    // живы, перезаливать их не надо — новому конверту нужны те же метаданные.
    const envelope = await this.client.encryptEnvelope(
      entry.text, {
        messageId: entry.messageId, sentAt: entry.sentAt,
        ...(entry.attachments?.length ? { attachments: entry.attachments } : {}),
        ...(entry.replyTo ? { replyTo: entry.replyTo } : {}),
      });
    const updated = { ...entry, envelope };
    await this.store.putOutbox(updated);
    try {
      const ack = await this.api.postEnvelope(envelope);
      await this._settleSent(updated, ack);
      return true;
    } catch (e) {
      // Тот же приговор, что в _postOutboxEntry (ревью ATT С2 р.2): блоба уже
      // нет — и с этого POST'а тоже. Без ветки здесь человек один лишний круг
      // видел бы «повторите» про файл, который не вернуть.
      // Как и в _postOutboxEntry — только по машинному коду (ATT C4): голый
      // 404 здесь = разговора/устройства нет, конверт остаётся, текст цел.
      if (e?.status === 404 && e?.detail?.code === 'attachment_missing') {
        return this._dropSweptAttachments(updated);
      }
      if (e?.status === 404) {
        this._emit({ error: { text: NO_CONVERSATION_TEXT, retriable: true } });
        return false;
      }
      this._emit({ error: { text: 'Устройство сотрудника сменилось, доставить не удалось — повторите', retriable: true } });
      return false;
    }
  }

  /** 🔴 Ревью ATT С2 р.1, High: блоб, не привязанный к сообщению сутки
   *  (конверт застрял в аутбоксе дольше окна сирот), сервер УБИРАЕТ — его 404
   *  не лечится ни ретраем, ни временем: шифротекста больше нет нигде,
   *  перезалить его не из чего. Ретрай был бы вечным враньём «проверьте
   *  связь»; конверт снимается, человеку — правда и действие. */
  async _dropSweptAttachments(entry) {
    await this.store.deleteOutbox(entry.messageId);
    this._emit({
      error: {
        text: 'Сообщение не ушло: файл ждал отправки дольше суток, и сервер '
          + 'уже удалил его. Прикрепите файл заново.',
        retriable: false,
      },
    });
    return true; // конверт снят — список изменился
  }

  async _settleSent(entry, ack) {
    await this.store.putMessage({
      conversationId: entry.conversationId, messageId: entry.messageId,
      seq: ack.seq, direction: 'out', text: entry.text, sentAt: entry.sentAt,
      attachments: entry.attachments ?? null,
      replyTo: entry.replyTo ?? null,
    });
    await this.store.deleteOutbox(entry.messageId);
    // lastSeq НЕ двигаем по ack: свой seq мог обогнать ещё не догнанный
    // конверт визави — курсор догона двигают только входящие строки.
    if (this.state.error?.retriable) this._emit({ error: null });
  }

  /** «Повторить» со страницы: перепостить зависший аутбокс и добрать догон. */
  retryPending() {
    return this._enqueue(async () => {
      await this._resendOutbox();
      await this._loadHistory();
    }).then(() => this.requestCatchUp());
  }

  /**
   * Связь вернулась (подписка встала): сначала ДОСЛАТЬ зависший аутбокс, потом
   * догон.
   *
   * 🔴 Зачем. Раньше `_resendOutbox` звался только из `_bootstrap` и из кнопки
   * «Повторить». Сообщение, отправленное без связи, не терялось (конверт лежит
   * в аутбоксе с тем же message_id, сервер дедупит), но само НЕ улетало при
   * возврате сети — только при следующем открытии чата или по кнопке. Человек
   * при этом видит «отправляется…» и не понимает, что от него ждут действия.
   *
   * Порядок именно такой: досыл встаёт в очередь ПЕРЕД догоном — иначе своё
   * сообщение появлялось бы в переписке после чужих, пришедших позже.
   * Порядок держится очередью: догон ставится ИЗ шага досыла (см. ниже).
   * Отдельного обработчика ошибок нет: причину уже показал `_postOutboxEntry`,
   * а отказ самого стора глотаем — на реконнекте это не повод рушить чат.
   */
  _flushOnReady() {
    // Проверки stopped здесь нет намеренно: её мутант не убивает — _enqueue уже
    // не пускает шаг после stop(), и _resendOutbox проверяет флаг на каждом
    // конверте. Дубль был бы непинуемым мёртвым кодом.
    this._enqueue(async () => {
      try {
        // 🔴 История перечитывается ТОЛЬКО если что-то реально ушло (ревью
        // Codex р.1 и р.2): обычный реконнект — это пустая очередь, а
        // неудачный досыл ничего в сторе не меняет. Лишний прогон стора с
        // перерисовкой на каждом восстановлении связи не нужен — догон и так
        // кончается _loadHistory.
        if (await this._resendOutbox()) await this._loadHistory();
      } finally {
        // 🔴 Догон ставится ОТСЮДА, а не рядом с этим шагом (долг QR-2 №2,
        // зеркало комнаты — там это Medium ревью Codex р.1). Снаружи
        // `requestCatchUp()` схлопнулся бы в догон, который УЖЕ стоит в
        // очереди с прошлого уведомления, — а тот стоит ВПЕРЕДИ досыла, и
        // обещанный порядок «сначала досыл» превращался бы в свою
        // противоположность ровно тогда, когда он и нужен: связь рвалась,
        // конверт завис, уведомление успело прийти до обрыва.
        //
        // Изнутри шага этой развилки нет: очередь последовательна, значит
        // прежний догон уже отработал и флаг снят. `finally` — потому что
        // упавший досыл не повод не забирать чужие сообщения.
        this.requestCatchUp();
      }
    }).catch(() => { /* показано в _postOutboxEntry либо отказ стора */ });
    // 🔴 .catch выше НЕ избыточен (в ревью это назвали лишним): _enqueue гасит
    // ошибку только в СВОЕЙ цепочке (`this._queue = next.catch(...)`), а
    // возвращает он `next` — тот реджектится и без обработчика даёт
    // unhandledrejection.
  }

  /**
   * @returns {Promise<number>} сколько конвертов РЕАЛЬНО ушло (или снято с
   * очереди сервером). Считаем именно это, а не попытки (ревью Codex р.2):
   * при неудачном досыле в сторе ничего не поменялось, и перечитывать историю
   * незачем — она нарисует то же самое.
   */
  async _resendOutbox() {
    const pending = await this.store.listOutbox(this.client.conversationId);
    let settled = 0;
    for (const entry of pending) {
      if (this.stopped) return settled;
      if (await this._postOutboxEntry(entry)) settled += 1;
    }
    return settled;
  }

  // ------------------------------------------------------------- приём

  /** Догон сериализуется той же очередью; повторный запрос во время работы
   *  схлопывается в один (уведомления SSE приходят пачками). Ошибка догона
   *  (сеть, отказ стора машины) не роняет ничего и не съедается молча:
   *  конверты остаются на сервере, честная плашка + повтор по следующему
   *  событию/реконнекту/кнопке. */
  requestCatchUp() {
    if (this._catchUpQueued) return this._queue;
    this._catchUpQueued = true;
    return this._enqueue(async () => {
      this._catchUpQueued = false;
      await this._catchUp();
    }).catch(() => {
      this._emit({ error: { text: 'Приём сообщений прервался — повторите или подождите переподключения', retriable: true } });
    });
  }

  async _catchUp() {
    const seqKey = `last-seq:${this.client.conversationId}`;
    let after = (await this.store.getKV(seqKey)) ?? 0;
    for (;;) {
      const page = await this.api.catchUp(this.client.conversationId, after);
      if (!page.envelopes.length) break;
      for (const row of page.envelopes) {
        if (this.stopped) return;
        await this._noteSenderDevice(row); // доверие обновляется ДО рендера
        // Битый конверт изолирует сам receiveEnvelopes (р.1 High-2, сужено в
        // р.2): он даёт «не расшифровано» ТОЛЬКО на мусорный cipher.body.
        // Отказ машины/стора отсюда ПРОБРАСЫВАЕТСЯ — конверт не «сгорает»,
        // last-seq и курсор не двигаются, догон повторится (requestCatchUp).
        const [m] = await this.client.receiveEnvelopes([row]);
        // Урок 3: отправитель — строго визави или я сам; чужой матрикс-id в
        // расшифровке = кривой конверт, записываем как нерасшифрованный.
        const expected = [this.client.peerMatrixId, this.client.matrixId];
        const trusted = m.plaintext !== null && expected.includes(m.senderMatrixId);
        const text = trusted ? m.plaintext : null;
        // ATT С2: вложения — только из ДОВЕРЕННОГО события и только валидной
        // формы; кап рендера — зеркало серверного (тысяча метаданных в
        // шифротексте заказала бы тысячу скачиваний).
        const atts = trusted && Array.isArray(m.attachments)
          ? m.attachments.filter(validMeta).slice(0, MAX_ATTACHMENTS_PER_MESSAGE)
          : [];
        // WEB-16: цитата — та же посадка, что у вложений. Только из
        // ДОВЕРЕННОГО события и только разрешённой формы; негодная
        // отбрасывается целиком, и сообщение показывается как обычное.
        // `selfMessageId` закрывает ответ сам на себя (цикл при переходе).
        const quote = trusted
          ? normalizeReplyTo(m.replyTo, { selfMessageId: row.message_id })
          : null;
        // Ретрай страницы после сбоя курсора гонит те же конверты через
        // машину ПОВТОРНО — replay-дроп Olm отдаёт null, и перезапись строки
        // стёрла бы уже расшифрованный текст. null никогда не затирает ни
        // текст, ни вложения.
        const existing = await this.store.getMessage(
          this.client.conversationId, row.message_id);
        await this.store.putMessage({
          conversationId: this.client.conversationId, messageId: row.message_id,
          seq: row.seq, direction: 'in', text: text ?? existing?.text ?? null,
          attachments: atts.length ? atts : existing?.attachments ?? null,
          // Тот же запрет затирания, что у текста и вложений: повторный
          // прогон конверта через машину даёт replay-дроп (null), и цитата,
          // уже разобранная в прошлый раз, не должна пропадать.
          replyTo: quote ?? existing?.replyTo ?? null,
          sentAt: row.sent_at ?? null,
        });
        after = Math.max(after, row.seq);
      }
      // Порядок: СНАЧАЛА delivered-курсор серверу, потом локальный last-seq
      // (ревью р.3, Medium). Иначе отказ postCursor оставлял бы серверный
      // курсор позади навсегда: следующий догон стартует с нового локального
      // after и этот диапазон seq больше никогда не репортится.
      await this.api.postCursor({
        conversation_id: this.client.conversationId,
        kind: 'delivered', seq: after,
      });
      await this.store.setKV(seqKey, after);
    }
    await this._loadHistory();
  }

  // ------------------------------------------------------------- SSE

  async _streamLoop() {
    let backoff = 1000;
    while (!this.stopped) {
      try {
        const stream = this.api.openStream({
          onNotify: (n) => {
            if (n.conversation_id === this.client.conversationId) this.requestCatchUp();
          },
        });
        this.stream = stream;
        await stream.ready;
        backoff = 1000;
        // Д-17: поток встал (в т.ч. после рестарта бэкенда окном) — спросить,
        // не сменился ли бандл. Ответа не ждём, досыл не задерживаем.
        noteStreamReady();
        this._flushOnReady(); // §5.1: подписка -> ready -> досыл и догон
        await stream.closed;
      } catch (e) {
        if (this.stopped) return;
        if (e?.status === 401) {
          // Токен протух: интерсептор axios обновит его на любом REST-вызове
          try { await this.api.poke(); } catch { /* refresh не встал — ниже реконнект */ }
        } else if (e?.status === 403) {
          this._emit({ phase: 'error', error: { text: 'Нет доступа к чату', retriable: false } });
          return;
        }
      }
      if (this.stopped) return;
      await this._sleep(backoff + Math.floor(Math.random() * 500));
      backoff = Math.min(backoff * 2, RECONNECT_MAX_MS);
    }
  }

  // ------------------------------------------------------------- стоп

  /** Отменяемая пауза реконнекта: stop() будит её сразу, не оставляя
   *  живой таймер после ухода со страницы. */
  _sleep(ms) {
    return new Promise((resolve) => {
      this._sleepResolve = resolve;
      this._sleepTimer = setTimeout(resolve, ms);
    });
  }

  /**
   * Остановить и дождаться, пока ДОЕДЕТ уже начатая работа с машиной/стором
   * (очередь _enqueue: bootstrap, догон, отправка). Возвращает промис
   * «всё улеглось» — страница держит Web Lock до него (ревью QR-1b р.2,
   * High): иначе новый контроллер на том же сторе стартует, пока старый ещё
   * пишет в IndexedDB. Никогда не реджектится.
   */
  stop() {
    this.stopped = true;
    clearTimeout(this._sleepTimer);
    this._sleepResolve?.();
    this.stream?.close();
    // Закрытие стора — ПОСЛЕ хвоста очереди: IDBDatabase.close() дожидается
    // транзакций, но открытые в самой очереди шаги не должны получать закрытый стор
    const settled = this._queue.then(() => undefined, () => undefined);
    return settled.then(() => {
      try { this.store?.close(); } catch { /* стор уже закрыт/не открылся — лок всё равно отпускаем */ }
      // Крипто-стор закрывается вместе с машиной: без этого его соединение
      // живёт до перезагрузки страницы и блокирует deleteDatabase (Д-3, E3).
      this.client?.close?.();
    });
  }
}
